/**
 * RhCarrierePanel — carrière d'un agent (items 5.12 et 5.14, mig 237).
 *
 * S'ouvre depuis la liste des dossiers agents. Trois blocs :
 *   · les anciennetés, calculées (jamais stockées) ;
 *   · l'historique des grades et échelons, avec le prochain avancement ;
 *   · l'historique des contrats, avec l'alerte de cumul CDD.
 *
 * L'alerte de requalification est INFORMATIVE : l'écran le dit explicitement.
 * VPC ne requalifie personne — c'est une décision d'employeur.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Card, Button, Badge, Alert, Field } from '@/components/ui';
import {
  ArrowLeft, Plus, Trash2, Loader2, AlertCircle, TrendingUp, FileSignature, Info,
  ShieldPlus, Users,
} from 'lucide-react';

const INPUT_CLS =
  'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh focus:ring-offset-2 focus:ring-offset-bg';

const UNITES = [
  { v: 'jours', l: 'jour(s)' },
  { v: 'semaines', l: 'semaine(s)' },
  { v: 'mois', l: 'mois' },
  { v: 'annees', l: 'année(s)' },
] as const;

const TYPES_CONTRAT = [
  { v: 'cdd', l: 'CDD' },
  { v: 'cdi', l: 'CDI' },
  { v: 'stage', l: 'Stage' },
  { v: 'apprentissage', l: 'Apprentissage' },
  { v: 'titulaire', l: 'Titularisation' },
] as const;

/** Ancienneté lisible : « 7 ans et 6 mois » plutôt que « 90 mois ». */
function ancienneteLisible(mois: number | null): string {
  if (mois === null) return 'non renseignée';
  const ans = Math.floor(mois / 12);
  const reste = mois % 12;
  if (ans === 0) return `${reste} mois`;
  if (reste === 0) return `${ans} an${ans > 1 ? 's' : ''}`;
  return `${ans} an${ans > 1 ? 's' : ''} et ${reste} mois`;
}

function dateFr(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

interface Avancement {
  id: string; grade: string | null; echelon: number | null;
  dateEffet: string; dateProchainAvancement: string | null;
  commentaire: string | null;
}
interface Contrat {
  id: string; typeContrat: string; dateDebut: string; dateFin: string | null;
  motif: string | null; dureeMois: number | null;
}
interface Anciennetes {
  communeMois: number | null; fonctionPubliqueMois: number | null;
  dateEntreeCommune: string | null; dateEntreeFonctionPublique: string | null;
}
interface Social {
  pscPrevoyance: boolean; pscPrevoyanceEcheance: string | null;
  pscSante: boolean; pscSanteEcheance: string | null;
  cnasAdherent: boolean; cnasSecteur: string | null; cnasEcheance: string | null;
}
interface Sft {
  actif: boolean; nombreEnfants: number | null;
  dateDebut: string | null; dateFin: string | null; arretManuel: string | null;
  montantMensuelCents: number | null; dateFinEffective: string | null; enCours: boolean;
}
interface Risque {
  cumulMois: number; seuilMois: number; seuilAtteint: boolean; seuilProche: boolean;
}

export function RhCarrierePanel({
  agentId, agentNom, onRetour,
}: {
  agentId: string;
  agentNom: string;
  onRetour: () => void;
}) {
  const [avancements, setAvancements] = useState<Avancement[]>([]);
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [anciennetes, setAnciennetes] = useState<Anciennetes | null>(null);
  const [risque, setRisque] = useState<Risque | null>(null);
  const [social, setSocial] = useState<Social | null>(null);
  const [sft, setSft] = useState<Sft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formAv, setFormAv] = useState(false);
  const [grade, setGrade] = useState('');
  const [echelon, setEchelon] = useState('');
  const [dateEffet, setDateEffet] = useState('');
  const [dateProchain, setDateProchain] = useState('');
  const [rapQte, setRapQte] = useState('3');
  const [rapUnite, setRapUnite] = useState<string>('mois');

  const [formCt, setFormCt] = useState(false);
  const [typeContrat, setTypeContrat] = useState('cdd');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [motif, setMotif] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [av, ct, an, rq, so, sf] = await Promise.all([
        api.rh.carriere.avancements(agentId),
        api.rh.carriere.contrats(agentId),
        api.rh.carriere.anciennetes(agentId),
        api.rh.carriere.requalification(agentId),
        api.rh.carriere.social(agentId),
        api.rh.carriere.sft(agentId),
      ]);
      setAvancements(av.avancements); setContrats(ct.contrats);
      setAnciennetes(an); setRisque(rq); setSocial(so); setSft(sf);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [agentId]);

  useEffect(() => { void charger(); }, [charger]);

  async function ajouterAvancement() {
    if (!dateEffet) return;
    setSaving(true); setError(null);
    try {
      await api.rh.carriere.creerAvancement(agentId, {
        grade: grade.trim() || null,
        echelon: echelon ? Number(echelon) : null,
        dateEffet,
        dateProchainAvancement: dateProchain || null,
        rappels: [{ quantite: Number(rapQte) || 3, unite: rapUnite }],
      });
      setFormAv(false); setGrade(''); setEchelon('');
      setDateEffet(''); setDateProchain('');
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function ajouterContrat() {
    if (!dateDebut) return;
    setSaving(true); setError(null);
    try {
      await api.rh.carriere.creerContrat(agentId, {
        typeContrat,
        dateDebut,
        dateFin: dateFin || null,
        motif: motif.trim() || null,
      });
      setFormCt(false); setDateDebut(''); setDateFin(''); setMotif('');
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  const cdiSansFin = ['cdi', 'titulaire'].includes(typeContrat);

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <button type="button" onClick={onRetour}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Dossiers agents
      </button>

      <h2 className="text-lg font-semibold text-ink">Carrière — {agentNom}</h2>

      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* ── Anciennetés (5.12) ── */}
      <Card variant="glass" padded>
        <h3 className="mb-2 text-sm font-medium text-ink">Anciennetés</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-r-lg border border-border bg-surface/40 p-3">
            <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Dans la commune
            </p>
            <p className="mt-0.5 text-sm font-medium text-ink">
              {ancienneteLisible(anciennetes?.communeMois ?? null)}
            </p>
            <p className="text-2xs text-ink-muted">
              depuis le {dateFr(anciennetes?.dateEntreeCommune ?? null)}
            </p>
          </div>
          <div className="rounded-r-lg border border-border bg-surface/40 p-3">
            <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Fonction publique territoriale
            </p>
            <p className="mt-0.5 text-sm font-medium text-ink">
              {ancienneteLisible(anciennetes?.fonctionPubliqueMois ?? null)}
            </p>
            <p className="text-2xs text-ink-muted">
              depuis le {dateFr(anciennetes?.dateEntreeFonctionPublique ?? null)}
            </p>
          </div>
        </div>
        <p className="mt-2 text-2xs text-ink-muted">
          Calculées depuis les dates d'entrée du dossier — jamais stockées, donc
          jamais périmées.
        </p>
      </Card>

      {/* ── Avancements (5.12) ── */}
      <Card variant="glass" padded>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink">
            <TrendingUp className="h-4 w-4" aria-hidden="true" /> Grades et échelons
          </h3>
          {!formAv && (
            <Button variant="secondary" size="sm" onClick={() => setFormAv(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter une étape
            </Button>
          )}
        </div>

        {formAv && (
          <div className="mb-3 space-y-2 rounded-r-lg border border-border bg-surface/40 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Grade" htmlFor="car-grade">
                <input id="car-grade" value={grade} onChange={(e) => setGrade(e.target.value)}
                  maxLength={150} className={INPUT_CLS} />
              </Field>
              <Field label="Échelon" htmlFor="car-echelon">
                <input id="car-echelon" type="number" min={1} max={20} value={echelon}
                  onChange={(e) => setEchelon(e.target.value)} className={INPUT_CLS} />
              </Field>
              <Field label="Date d'effet *" htmlFor="car-effet">
                <input id="car-effet" type="date" value={dateEffet}
                  onChange={(e) => setDateEffet(e.target.value)} className={INPUT_CLS} />
              </Field>
              <Field label="Prochain avancement prévu" htmlFor="car-prochain">
                <input id="car-prochain" type="date" value={dateProchain}
                  onChange={(e) => setDateProchain(e.target.value)} className={INPUT_CLS} />
              </Field>
            </div>

            {/* 5.12 — « rappels multi-paramétrables (nombre + unité) » */}
            {dateProchain && (
              <div>
                <p className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  Me prévenir avant
                </p>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} value={rapQte}
                    onChange={(e) => setRapQte(e.target.value)}
                    className={`${INPUT_CLS} w-20`} />
                  <select value={rapUnite} onChange={(e) => setRapUnite(e.target.value)}
                    className={`${INPUT_CLS} w-40`}>
                    {UNITES.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="primary" size="sm" loading={saving}
                disabled={!dateEffet} onClick={() => void ajouterAvancement()}>
                Enregistrer
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFormAv(false)}>
                Annuler
              </Button>
            </div>
          </div>
        )}

        {avancements.length === 0 ? (
          <p className="py-3 text-center text-sm text-ink-muted">
            Aucune étape enregistrée.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {avancements.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-ink">
                    {a.grade ?? 'Grade non précisé'}
                    {a.echelon !== null && ` — échelon ${a.echelon}`}
                  </p>
                  <p className="text-2xs text-ink-muted">
                    Effet le {dateFr(a.dateEffet)}
                    {a.dateProchainAvancement &&
                      ` · prochain avancement le ${dateFr(a.dateProchainAvancement)}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={async () => {
                    await api.rh.carriere.supprimerAvancement(a.id);
                    await charger();
                  }}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── Contrats (5.14) ── */}
      <Card variant="glass" padded>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink">
            <FileSignature className="h-4 w-4" aria-hidden="true" /> Contrats
          </h3>
          {!formCt && (
            <Button variant="secondary" size="sm" onClick={() => setFormCt(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter un contrat
            </Button>
          )}
        </div>

        {/* L'alerte de cumul — informative, et l'écran le dit. */}
        {risque && risque.cumulMois > 0 && (
          <Alert
            variant={risque.seuilAtteint ? 'warning' : risque.seuilProche ? 'warning' : 'info'}
            icon={Info}
            className="mb-3"
          >
            Cumul des contrats à durée déterminée : <strong>{risque.cumulMois} mois</strong>
            {' '}pour un seuil paramétré à {risque.seuilMois} mois.
            {risque.seuilAtteint && (
              <> Le seuil est atteint : un renouvellement à l'identique est à examiner
              juridiquement.</>
            )}
            {risque.seuilProche && <> Anticipez la décision de renouvellement.</>}
            <span className="mt-1 block text-2xs">
              VPC signale, il ne requalifie pas : la décision appartient à la collectivité.
            </span>
          </Alert>
        )}

        {formCt && (
          <div className="mb-3 space-y-2 rounded-r-lg border border-border bg-surface/40 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Type *" htmlFor="ct-type">
                <select id="ct-type" value={typeContrat}
                  onChange={(e) => setTypeContrat(e.target.value)} className={INPUT_CLS}>
                  {TYPES_CONTRAT.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Field>
              <Field label="Début *" htmlFor="ct-debut">
                <input id="ct-debut" type="date" value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)} className={INPUT_CLS} />
              </Field>
              {/* Un CDI n'a pas de fin : on masque le champ plutôt que de le
                  laisser saisir pour un refus serveur ensuite. */}
              {!cdiSansFin && (
                <Field label="Fin" htmlFor="ct-fin">
                  <input id="ct-fin" type="date" value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)} className={INPUT_CLS} />
                </Field>
              )}
              <Field label="Motif de recours" htmlFor="ct-motif">
                <input id="ct-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                  maxLength={300} placeholder="Remplacement, accroissement temporaire…"
                  className={INPUT_CLS} />
              </Field>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" loading={saving}
                disabled={!dateDebut} onClick={() => void ajouterContrat()}>
                Enregistrer
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFormCt(false)}>
                Annuler
              </Button>
            </div>
          </div>
        )}

        {contrats.length === 0 ? (
          <p className="py-3 text-center text-sm text-ink-muted">
            Aucun contrat enregistré.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {contrats.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm text-ink">
                    {TYPES_CONTRAT.find((t) => t.v === c.typeContrat)?.l ?? c.typeContrat}
                    {c.dureeMois !== null && (
                      <Badge variant="neutral" size="sm">{c.dureeMois} mois</Badge>
                    )}
                  </p>
                  <p className="text-2xs text-ink-muted">
                    Du {dateFr(c.dateDebut)}
                    {c.dateFin ? ` au ${dateFr(c.dateFin)}` : ' — sans échéance'}
                    {c.motif && ` · ${c.motif}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={async () => {
                    await api.rh.carriere.supprimerContrat(c.id);
                    await charger();
                  }}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── Protection sociale et CNAS (5.16 / 5.17) ── */}
      {social && (
        <Card variant="glass" padded>
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-medium text-ink">
            <ShieldPlus className="h-4 w-4" aria-hidden="true" /> Protection sociale
          </h3>

          <div className="grid gap-2 sm:grid-cols-2">
            {/* Prévoyance et santé sont DEUX contrats : deux dates, deux
                relances. Les fusionner ferait relancer les deux ensemble. */}
            <LigneSociale
              titre="Prévoyance" actif={social.pscPrevoyance}
              echeance={social.pscPrevoyanceEcheance}
              onToggle={async (v) => {
                await api.rh.carriere.majSocial(agentId, { pscPrevoyance: v });
                await charger();
              }}
              onDate={async (d) => {
                await api.rh.carriere.majSocial(agentId, { pscPrevoyanceEcheance: d || null });
                await charger();
              }}
            />
            <LigneSociale
              titre="Santé" actif={social.pscSante}
              echeance={social.pscSanteEcheance}
              onToggle={async (v) => {
                await api.rh.carriere.majSocial(agentId, { pscSante: v });
                await charger();
              }}
              onDate={async (d) => {
                await api.rh.carriere.majSocial(agentId, { pscSanteEcheance: d || null });
                await charger();
              }}
            />
            <LigneSociale
              titre="CNAS" actif={social.cnasAdherent}
              echeance={social.cnasEcheance}
              complement={social.cnasSecteur}
              onToggle={async (v) => {
                await api.rh.carriere.majSocial(agentId, { cnasAdherent: v });
                await charger();
              }}
              onDate={async (d) => {
                await api.rh.carriere.majSocial(agentId, { cnasEcheance: d || null });
                await charger();
              }}
            />
          </div>
        </Card>
      )}

      {/* ── Supplément familial (5.19) ── */}
      {sft && (
        <Card variant="glass" padded>
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-medium text-ink">
            <Users className="h-4 w-4" aria-hidden="true" /> Supplément familial
          </h3>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={sft.actif}
                onChange={async (e) => {
                  await api.rh.carriere.majSft(agentId, { actif: e.target.checked });
                  await charger();
                }} />
              Droit ouvert
            </label>
            {sft.actif && (
              <>
                <span className="text-xs text-ink-muted">
                  {sft.nombreEnfants ?? 0} enfant{(sft.nombreEnfants ?? 0) > 1 ? 's' : ''}
                </span>
                {sft.montantMensuelCents !== null ? (
                  <Badge variant="info" size="sm">
                    {(sft.montantMensuelCents / 100).toFixed(2)} € / mois
                  </Badge>
                ) : (
                  <span className="text-2xs text-ink-muted">
                    Montant par enfant non paramétré dans les réglages RH
                  </span>
                )}
                {!sft.enCours && <Badge variant="neutral" size="sm">Éteint</Badge>}
              </>
            )}
          </div>

          {sft.actif && (
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <Field label="Début" htmlFor="sft-debut">
                <input id="sft-debut" type="date" value={sft.dateDebut ?? ''}
                  onChange={async (e) => {
                    await api.rh.carriere.majSft(agentId, {
                      actif: true, dateDebut: e.target.value || null,
                    });
                    await charger();
                  }} className={INPUT_CLS} />
              </Field>
              <Field label="Fin prévue" htmlFor="sft-fin">
                <input id="sft-fin" type="date" value={sft.dateFin ?? ''}
                  onChange={async (e) => {
                    await api.rh.carriere.majSft(agentId, {
                      actif: true, dateFin: e.target.value || null,
                    });
                    await charger();
                  }} className={INPUT_CLS} />
              </Field>
              <Field label="Arrêt anticipé" htmlFor="sft-arret">
                <input id="sft-arret" type="date" value={sft.arretManuel ?? ''}
                  onChange={async (e) => {
                    await api.rh.carriere.majSft(agentId, {
                      actif: true, arretManuel: e.target.value || null,
                    });
                    await charger();
                  }} className={INPUT_CLS} />
              </Field>
            </div>
          )}

          {sft.arretManuel && (
            <p className="mt-2 text-2xs text-ink-muted">
              L'arrêt anticipé prime sur la fin prévue : le droit s'éteint le{' '}
              {dateFr(sft.arretManuel)}.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

/** Une ligne « case + échéance » — le même geste pour prévoyance, santé, CNAS. */
function LigneSociale({
  titre, actif, echeance, complement, onToggle, onDate,
}: {
  titre: string;
  actif: boolean;
  echeance: string | null;
  complement?: string | null;
  onToggle: (v: boolean) => Promise<void>;
  onDate: (d: string) => Promise<void>;
}) {
  return (
    <div className="rounded-r-lg border border-border bg-surface/40 p-3">
      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={actif}
          onChange={(e) => void onToggle(e.target.checked)} />
        {titre}
      </label>
      {complement && (
        <p className="mt-0.5 text-2xs text-ink-muted">{complement}</p>
      )}
      {/* L'échéance ne sert à rien sans adhésion : on ne l'affiche pas. */}
      {actif && (
        <input type="date" value={echeance ?? ''}
          onChange={(e) => void onDate(e.target.value)}
          className={`${INPUT_CLS} mt-2`} aria-label={`Échéance ${titre}`} />
      )}
    </div>
  );
}
